import React, {
  ReactElement,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react"
import { Box } from "@mui/material"
import {
  TableContainer,
  TableEmptyPlaceholder,
  TableFooter,
  TableHeadComponent,
  TableRowComponent
} from "./components"
import VirtualTableContext from "./VirtualTableContext"
import {
  useAdjustMinHeightForScroll,
  useHasHorizontalScroll,
  useHeaderAndBodyScrollSync,
  useTableLogic,
  useVirtualTable
} from "./hooks"
import {
  Column,
  ScrollProps,
  SortProps,
  SortTypes,
  TableRecordType
} from "./types"
import { useStyles } from "./styles"
import { flattenTableColumns } from "./helpers"

export type SelectionProps = {
  selected?: string[]
  allSelected?: boolean
  onToggle?: (id: string) => void
  onToggleAll?: (selectAll: boolean) => void
  onDeselectAll?: () => void
}

export type TableClassesType =
  | "container"
  | "tableWrapper"
  | "table"
  | "headerTable"
  | "headerSticky"
  | "actionsSticky"
  | "firstDiagonalColumn"
  | "tableCellDiagonal"
  | "firstColumn"
  | "body"
  | "tableWrapperLoading"

export type CustomEmptyMessage = {
  title: string
  text: React.ReactNode
  icon?: string
  iconColor?: string
  iconBgColor?: string
}

export type Props = SelectionProps &
  SortProps &
  ScrollProps & {
    idProp?: string
    size?: "sm" | "lg"
    tableCaption?: string
    headerColumns: Column[]
    displayedProperties?: string[]
    records: TableRecordType[]
    loading?: boolean
    loadingComponent?: React.ReactNode
    emptyPlaceholder?: boolean
    className?: string
    dataAutomationId?: string
    selectable?: boolean
    expandable?: boolean
    renderPlaceholder?: (columns: number) => React.ReactNode
    onSelectDisplayedProperty?: (name: string) => void
    onRowClick?: (id: string, record: any, isTargetBlank?: boolean) => void
    getRowAction?: (
      id: string,
      record?: { [key: string]: string }
    ) => ReactElement
    isSelectionDisabled?: (record: TableRecordType) => boolean
    children?: (
      prop: string,
      record: TableRecordType,
      cellValue?: unknown
    ) => React.ReactNode
    classes?: Partial<Record<TableClassesType, string>>
    entityLabel?: string
    searchString?: string
    hasActiveFilters?: boolean
    tableHeight?: number
    stickyHeader?: boolean
    stickyFirstColumn?: boolean
    customEmptyCheck?: () => boolean
    customEmptyMessage?: CustomEmptyMessage
    hideFooter?: boolean
    // container-based virtualization instead of window-based
    useContainerScroll?: boolean

    // custom renderer for selection cell (instead of default checkbox)
    renderSelectionCell?: (params: {
      record: TableRecordType
      isSelected: boolean
      disabled: boolean
      onToggle: () => void
    }) => React.ReactNode
  }

const Table: React.FC<Props> = ({
  idProp = "id",
  size = "lg",
  tableCaption,
  selected,
  allSelected,
  headerColumns,
  displayedProperties,
  records = [],
  loading,
  loadingComponent,
  emptyPlaceholder = true,
  selectable = false,
  expandable = false,
  className,
  sortBy,
  order,
  onSort,
  onSelectDisplayedProperty,
  renderPlaceholder,
  onToggle,
  onToggleAll,
  onRowClick,
  getRowAction,
  isSelectionDisabled,
  hasMoreItems,
  onFetch,
  children,
  dataAutomationId,
  classes: outerClasses,
  searchString,
  hasActiveFilters = false,
  tableHeight,
  stickyHeader = false,
  stickyFirstColumn = false,
  customEmptyCheck,
  customEmptyMessage,
  hideFooter = false,
  useContainerScroll = false,
  renderSelectionCell
}) => {
  const styles = useStyles({ size, loading })
  const { classes, cx } = styles
  const [sortAnnouncement, setSortAnnouncement] = useState<string>("")

  const headerContainerRef = useRef<HTMLDivElement>(null)

  // Flatten grouped columns for table display (grouping only for dropdown menu)
  const flattenedHeaderColumns = useMemo(
    () => flattenTableColumns(headerColumns),
    [headerColumns]
  )

  // Use shared table logic (uses original grouped columns for dropdown)
  const { isEmpty, totalColumnsCount, emptyPlaceholderType, dropdownOptions } =
    useTableLogic({
      records,
      loading,
      headerColumns,
      displayedProperties,
      selectable,
      getRowAction,
      hasActiveFilters,
      searchString,
      onSelectDisplayedProperty
    })

  const virtualTable = useVirtualTable({
    headerColumns: flattenedHeaderColumns, // Use flattened columns for table
    displayedProperties,
    records,
    size,
    sortBy,
    order,
    onSort,
    loading,
    hasMoreItems,
    onFetch,
    expandable,
    useContainerScroll
  })

  const {
    tableContainerRef,
    table,
    rowVirtualizer,
    onSort: onSortChange
  } = virtualTable

  const hasDiagonalHeaders = useMemo(() => {
    return headerColumns.some(col => col.diagonalHeader === true)
  }, [headerColumns])

  const { hasHorizontalScroll } = useHeaderAndBodyScrollSync(
    headerContainerRef,
    tableContainerRef
  )

  const sortedColumnAnnouncement = useCallback(
    (sortBy: string, order: SortTypes) => {
      const sortedColumn =
        headerColumns.find(c => (c.sortableName || c.name) === sortBy) ?? null
      const label = sortedColumn?.displayLabel || sortedColumn?.label || sortBy

      const sortDirectionText =
        order === SortTypes.asc ? "ascending" : "descending"

      return `${label}. Sorting ${sortDirectionText}.`
    },
    [headerColumns]
  )

  const handleSort = useCallback(
    (newSortBy: string, newOrder: SortTypes) => {
      const announcement = sortedColumnAnnouncement(newSortBy, newOrder)
      setSortAnnouncement(announcement)

      onSortChange(newSortBy, newOrder)
    },
    [onSortChange, sortedColumnAnnouncement]
  )

  const hasScroll = useHasHorizontalScroll(
    tableContainerRef as RefObject<HTMLElement>,
    hasDiagonalHeaders
  )

  useAdjustMinHeightForScroll(headerContainerRef, hasScroll, 6)

  return (
    <TableContainer styles={styles} outerClasses={outerClasses}>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {sortAnnouncement}
      </Box>
      <Box
        role="table"
        aria-label={tableCaption}
        className={cx(
          classes.table,
          outerClasses?.table,
          outerClasses?.headerTable
        )}
      >
        <VirtualTableContext.Provider value={virtualTable}>
          <Box
            ref={headerContainerRef}
            display={isEmpty ? "none" : "inherit"}
            className={cx(
              {
                [classes.headerSticky]: stickyHeader,
                [classes.headerStickyWithDiagonal]:
                  stickyHeader && hasDiagonalHeaders
              },
              outerClasses?.headerSticky
            )}
          >
            <Box className={classes.headerContainer}>
              <TableHeadComponent
                selected={allSelected}
                headerColumns={flattenedHeaderColumns}
                displayedProperties={displayedProperties}
                selectable={selectable}
                sortBy={sortBy}
                order={order}
                onSort={handleSort}
                onSelectAllRows={onToggleAll}
                getRowAction={getRowAction}
                dropdownOptions={dropdownOptions}
                hideHeaderSelection={Boolean(renderSelectionCell)}
                hasDiagonalHeaders={hasDiagonalHeaders}
                stickyFirstColumn={stickyFirstColumn}
              />
            </Box>
          </Box>

          <Box
            ref={tableContainerRef}
            className={cx(
              classes.tableWrapper,
              {
                [classes.tableWrapperLoading]: loading,
                [outerClasses?.tableWrapperLoading]: loading
              },
              outerClasses?.table,
              className
            )}
            style={
              useContainerScroll
                ? {
                    overflowY: "auto", // Required for container-based virtualization
                    overflowX: loading ? "hidden" : "auto"
                  }
                : undefined
            }
          >
            <Box
              role="rowgroup"
              className={cx(classes.body, { [outerClasses?.body]: !isEmpty })}
              style={{
                height: isEmpty
                  ? "auto"
                  : `${tableHeight || rowVirtualizer.getTotalSize()}px`
              }}
            >
              {/* Don't sort records inside. */}
              {rowVirtualizer.getVirtualItems().map(virtualRow => {
                const row = table.getRowModel().rows[virtualRow.index]
                return (
                  <TableRowComponent
                    hasHorizontalScroll={hasHorizontalScroll}
                    key={row.id}
                    index={virtualRow.index}
                    idProp={idProp}
                    record={row.original}
                    selectedRows={selected}
                    displayedProperties={displayedProperties}
                    headerColumns={headerColumns}
                    selectable={selectable}
                    onClick={onRowClick}
                    onSelectRow={onToggle}
                    getRowAction={getRowAction}
                    hasDiagonalHeaders={hasDiagonalHeaders}
                    stickyFirstColumn={stickyFirstColumn}
                    selectDisabled={
                      isSelectionDisabled?.(row.original) ?? false
                    }
                    renderSelectionCell={renderSelectionCell}
                  >
                    {children}
                  </TableRowComponent>
                )
              })}
              <TableEmptyPlaceholder
                isEmpty={isEmpty}
                emptyPlaceholder={emptyPlaceholder}
                totalColumnsCount={totalColumnsCount}
                emptyPlaceholderType={emptyPlaceholderType}
                renderPlaceholder={renderPlaceholder}
                customEmptyCheck={customEmptyCheck}
                customEmptyMessage={customEmptyMessage}
              />
            </Box>
          </Box>
        </VirtualTableContext.Provider>
      </Box>
      {loading && loadingComponent}
      <TableFooter recordsCount={records.length} hideFooter={hideFooter} />
    </TableContainer>
  )
}

export default React.memo(Table)
